import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { Event } from '../models/event.model';
import { BackendEventResponse, TokenDecodeResponse, RegisterableEventResponse } from '../models/backend-response.model';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private apiUrl = environment.apiUrl;
  private companyId = environment.companyId;

  constructor(private http: HttpClient) {}

  getEvent(eventId: number | string): Observable<Event> {
    const url = `${this.apiUrl}/api/companies/${this.companyId}/events/${eventId}`;
    return this.http.get<BackendEventResponse>(url).pipe(
      map(event => this.mapEventFromBackend(event))
    );
  }

  getEventByToken(token: string): Observable<Event> {
    const url = `${this.apiUrl}/api/event_tokens/decode`;
    return this.http.post<TokenDecodeResponse>(url, { token, type: 'attendee_register' }).pipe(
      map(response => {
        const eventData = response.event;
        
        if (!eventData) {
          throw new Error('Event data not found in token decode response');
        }
        
        const primarySpeaker = eventData.speakers && eventData.speakers.length > 0 ? eventData.speakers[0] : null;
        
        const topicName = typeof eventData.topic === 'string' 
          ? eventData.topic 
          : eventData.topic?.name;
        const topicId = typeof eventData.topic === 'string' 
          ? undefined 
          : eventData.topic?.id;
        const meetingTypeName = typeof eventData.meeting_type === 'string'
          ? eventData.meeting_type
          : eventData.meeting_type?.name;
        
        const mappedEvent: Event = {
          id: eventData.id || response.eventId || 0,
          name: eventData.name || '',
          topic: topicName,
          topic_id: topicId,
          venue_state: eventData.venue?.state,
          venue_name: eventData.venue?.name,
          venue_city: eventData.venue?.city,
          venue_zip: eventData.venue?.zip,
          start_at: eventData.start_at,
          end_at: eventData.end_at,
          start_day: eventData.start_at 
            ? new Date(eventData.start_at).toISOString().split('T')[0] 
            : undefined,
          end_day: eventData.end_at 
            ? new Date(eventData.end_at).toISOString().split('T')[0] 
            : undefined,
          start_time: eventData.start_at ? this.formatEventTime(eventData.start_at, eventData.timezone_id) : undefined,
          timezone: eventData.timezone_id,
          meeting_type: meetingTypeName,
          meeting_type_id: eventData.meeting_type_id,
          program_type: meetingTypeName,
          program_status: eventData.status?.id === 3 || eventData.status_id === 3 ? 'Confirmed' : undefined,
          speaker: primarySpeaker ? {
            id: primarySpeaker.id,
            first_name: primarySpeaker.first_name,
            last_name: primarySpeaker.last_name,
            full_name: `${primarySpeaker.first_name || ''} ${primarySpeaker.last_name || ''}`.trim(),
            degrees: primarySpeaker.degrees
          } : undefined,
          speaker_id: primarySpeaker?.id,
          venue: eventData.venue && eventData.venue.id ? {
            id: eventData.venue.id,
            name: eventData.venue.name || '',
            address1: eventData.venue.address1,
            address2: eventData.venue.address2,
            city: eventData.venue.city,
            state: eventData.venue.state,
            zip: eventData.venue.zip,
            general_phone: eventData.venue.general_phone,
            google_maps_uri: eventData.venue.google_maps_uri
          } : undefined,
          friendly_date: eventData.start_at 
            ? this.formatFriendlyDate(eventData.start_at) 
            : undefined,
          token: token,
          webcast_link: eventData.webcast_link,
          companyId: response.companyId
        };
        return mappedEvent;
      })
    );
  }

  searchEventByCode(code: string, state?: string): Observable<Event[]> {
    console.log(this.apiUrl);
    
    const url = `${this.apiUrl}/api/companies/${this.companyId}/events/registerable`;
    
    let normalizedCode = code.trim().toUpperCase();
    const match = code.match(/^(LX?|SX?)(\d+)$/i);
    
    if (match) {
      const prefix = match[1].toUpperCase();
      const num = match[2];
      
      if (prefix === 'LX' || prefix === 'SX') {
        normalizedCode = `${prefix}${num.padStart(4, '0')}`;
      } else if (prefix === 'L' || prefix === 'S') {
        normalizedCode = `${prefix}${num.padStart(5, '0')}`;
      }
    }
    
    const payload: any = {
      name: normalizedCode
    };
    if (state && state !== 'NA') {
      payload.venue_state = state;
    }
    
    return this.http.post<RegisterableEventResponse>(url, payload).pipe(
      map(response => {
        if (!response || !response.id) {
          return [];
        }
        return [{
          id: response.id,
          token: response.token,
          name: normalizedCode,
          venue_state: state || undefined
        } as Event];
      })
    );
  }

  private mapEventFromBackend(backendEvent: BackendEventResponse): Event {
    const commonEvent = backendEvent.common_event || {};
    const speakers = backendEvent.speakers_common_data || backendEvent.speakers || [];
    const primarySpeaker = speakers.length > 0 ? speakers[0] : null;
    
    const topicName = typeof backendEvent.topic === 'string' 
      ? backendEvent.topic 
      : backendEvent.topic?.name;
    const topicId = typeof backendEvent.topic === 'string' 
      ? undefined 
      : backendEvent.topic?.id;
    
    return {
      id: backendEvent.id || backendEvent.event_id || 0,
      name: commonEvent.name || backendEvent.name || '',
      topic: topicName,
      topic_id: topicId || backendEvent.topic_id,
      venue_state: backendEvent.venue?.state || commonEvent.venue?.state,
      venue_name: backendEvent.venue?.name,
      venue_city: backendEvent.venue?.city,
      venue_zip: backendEvent.venue?.zip,
      start_at: commonEvent.start_at,
      end_at: commonEvent.end_at,
      start_day: commonEvent.start_at ? new Date(commonEvent.start_at).toISOString().split('T')[0] : undefined,
      end_day: commonEvent.end_at ? new Date(commonEvent.end_at).toISOString().split('T')[0] : undefined,
      start_time: commonEvent.start_at ? this.formatEventTime(commonEvent.start_at, commonEvent.timezone_id) : undefined,
      timezone: commonEvent.timezone_id,
      meeting_type: backendEvent.meeting_type?.name,
      meeting_type_id: backendEvent.meeting_type_id,
      program_type: backendEvent.meeting_type?.name,
      program_status: backendEvent.status_id === 3 ? 'Confirmed' : undefined,
      webcast_link: backendEvent.webcast_link,
      speaker: primarySpeaker ? {
        id: primarySpeaker.id,
        first_name: primarySpeaker.first_name,
        last_name: primarySpeaker.last_name,
        full_name: `${primarySpeaker.first_name} ${primarySpeaker.last_name}`,
        degrees: primarySpeaker.degrees
      } : undefined,
      speaker_id: primarySpeaker?.id,
      venue: backendEvent.venue && backendEvent.venue.id ? {
        id: backendEvent.venue.id,
        name: backendEvent.venue.name || '',
        address1: backendEvent.venue.address1,
        address2: backendEvent.venue.address2,
        city: backendEvent.venue.city,
        state: backendEvent.venue.state,
        zip: backendEvent.venue.zip,
        general_phone: backendEvent.venue.general_phone
      } : undefined,
      below_cap: backendEvent.attendee_summary ? 
        (backendEvent.attendee_summary.preregistered || 0) < (backendEvent.attendee_cap || 999999) : true,
      friendly_date: commonEvent.start_at ? this.formatFriendlyDate(commonEvent.start_at) : undefined
    };
  }

  private formatEventTime(startAt: string, timezoneId?: string): string {
    if (!startAt) return '';
    const date = new Date(startAt);
    return date.toLocaleString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }

  private formatFriendlyDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  getRegistrationQuestions(eventId: number | string, token?: string): Observable<any[]> {
    let url = `${this.apiUrl}/api/companies/${this.companyId}/events/${eventId}/evaluations/registration_anonymous`;
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
    return this.http.get<any[]>(url);
  }

  /**
   * Fetch company dropdown options (credentials and specialty)
   * Uses: router.get('/dropdowns/:name', isAuthenticatedOrNot, companyDropdownCtrl.get);
   */
  getCompanyDropdown(companyId: number | string, name: string): Observable<Array<{ value: string; label: string }>> {
    const url = `${this.apiUrl}/api/companies/${companyId}/dropdowns/${name}`;
    return this.http.get<Array<{ value: string; label: string }>>(url);
  }

  /**
   * Fetch common dropdown options (states)
   * Uses: router.get('/dropdowns/:name', isAuthenticatedOrNot, dropdownCtrl.get);
   */
  getCommonDropdown(name: string): Observable<Array<{ value: string; label: string }>> {
    const url = `${this.apiUrl}/api/dropdowns/${name}`;
    return this.http.get<Array<{ value: string; label: string }>>(url);
  }
}

